import { useCallback, useEffect, useRef, useState } from 'react';
import { cloud } from './climbwriterCloud';
import { emptyPlan, readWritingPlan, type WritingPlan } from './writingPlan';

type State = { owner: string | null; plan: WritingPlan; version: number; ready: boolean; busy: boolean; error: string };
function cachePlan(key: string, plan: WritingPlan) {
  try {
    const previous = localStorage.getItem(key);
    const next = JSON.stringify(plan);
    if (previous && previous !== next) localStorage.setItem(`${key}:previous`, previous);
    localStorage.setItem(key, next);
  } catch { /* The saved account copy remains available. */ }
}
const timezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function useWritingPlan(owner: string | null, today: string, regularGoal: number, goalReady: boolean) {
  const key = `climbwriter-writing-plan:${owner ?? 'guest'}`;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const [state, setState] = useState<State>(() => ({ owner, plan: owner ? readWritingPlan(key) : emptyPlan(), version: 0, ready: false, busy: false, error: '' }));
  const inFlight = useRef(false);
  const generation = useRef(0);
  const reload = useCallback(async () => {
    if (!owner || !cloud) return;
    const request = ++generation.current;
    setState(previous => ({ ...previous, ready: false, error: '' }));
    const { data, error } = await Promise.resolve(cloud.rpc('climbwriter_writing_rhythm', { client_timezone: timezone() })).catch(() => ({ data: null, error: true }));
    if (currentOwner.current !== owner || request !== generation.current) return;
    if (error || !data) {
      setState(previous => ({ ...previous, owner, ready: false, error: 'Your account plan could not be loaded. Retry after the writing rhythm setup is available.' }));
      return;
    }
    const plan: WritingPlan = { weekStart: data.weekStart, days: data.days, goals: data.goals };
    cachePlan(key, plan);
    setState({ owner, plan, version: data.version, ready: true, busy: false, error: '' });
  }, [owner, key]);

  useEffect(() => {
    generation.current++;
    setState({ owner, plan: owner ? readWritingPlan(key) : emptyPlan(), version: 0, ready: false, busy: false, error: '' });
    if (owner && goalReady) void reload();
  }, [owner, key, goalReady, today, regularGoal, reload]);

  const save = async (plan: WritingPlan): Promise<boolean> => {
    if (!owner || !cloud || state.owner !== owner || !state.ready || inFlight.current) return false;
    inFlight.current = true;
    const request = ++generation.current;
    setState(previous => ({ ...previous, busy: true, error: '' }));
    try {
      const { data, error } = await cloud.rpc('climbwriter_writing_rhythm', {
        client_timezone: timezone(), new_days: plan.days, new_week_start: plan.weekStart, expected_version: state.version,
      });
      if (currentOwner.current !== owner || request !== generation.current) return false;
      if (error || !data) {
        setState(previous => ({ ...previous, busy: false, error: 'Your plan could not be saved. Reload it before trying again; your saved plan is unchanged.' }));
        return false;
      }
      const saved: WritingPlan = { weekStart: data.weekStart, days: data.days, goals: data.goals };
      cachePlan(key, saved);
      setState({ owner, plan: saved, version: data.version, ready: true, busy: false, error: '' });
      return true;
    } catch {
      if (currentOwner.current === owner && request === generation.current) setState(previous => ({ ...previous, busy: false, error: 'Your plan could not be saved. Check your connection and reload the plan.' }));
      return false;
    } finally { inFlight.current = false; }
  };
  return { plan: state.owner === owner ? state.plan : owner ? readWritingPlan(key) : emptyPlan(), ready: state.owner === owner && state.ready, busy: state.owner === owner && state.busy, error: state.owner === owner ? state.error : '', save, reload };
}
